import { client } from '@api/client';
import { URL } from '@api/urls';

import {
	ComparisonDetailsType,
	IBranchSelectedPaths,
	TCMOptions,
} from '@interfaces/cmModule/cmModule';
import { IBranchPaths, ICCOptionsProps } from '@interfaces/cmModule/configurationComparison';
import {
	IMoBranchesDTO,
	TCMConfigTypesDTO,
	TCMNodeVendorTechnology,
	TCMSitesIdsDTO,
	TCMVersionsDTO,
	TDetailsOptionsDTO,
	TMavenirDTO,
	TMoBranchDTO,
} from '@interfaces/cmModule/dto/cmModuleDTO';
import {
	ICcDatesDTO,
	TMoBranchesResponse,
	TMutateExportMoBranchDTO,
	TTreeCCModuleDTO,
} from '@interfaces/cmModule/dto/configurationComparisonDTO';
import { ICreatePresetDTO } from '@interfaces/cmModule/dto/presetsDTO';
import { IId, TIdName, TNameTitle } from '@interfaces/common/common';
import { IListOfTreePresets, IMoTreeResponse } from '@interfaces/components/tree';
import { IProgressBranches } from '@interfaces/import/import';

import {
	ESortingOptionValues,
	TVendorsValues,
} from '@enums/common';

const defaultSortParamsForPresetsOptions = {
	property: 'createdAt',
	sortDirection: ESortingOptionValues.desc,
};

const {
	configurationComparisonTechnologies,
	configurationComparisonVendors,
	nodeTypes,
	siteIdsOptions,
	datesCcModule,
	treeCcModule,
	moBranches,
	moBranchesProgress,
	moBranchesRun,
	exportBranches,
	exportBranch,
	createTreePreset,
	updateTreePreset,
	deleteTreePreset,
	treePresets,
	selectedPresetTree,
	configurationComparison,
	moBranchesGet,
} = URL;

export const getConfigurationComparisonTechnologies = (
	vendor: TVendorsValues,
): Promise<ICCOptionsProps[]> => {
	return client.post(configurationComparisonTechnologies, { vendor });
};

export const getConfigurationComparisonNodes = (vendor: TVendorsValues): Promise<string[]> => {
	return client.post(`/${configurationComparison}/nodes`, { vendor });
};

export const getConfigurationComparisonReleaseVersions = ({
	vendor,
	nodes,
}: TCMVersionsDTO): Promise<string[]> => {
	return client.post(`/${configurationComparison}/versions`, { vendor, nodes });
};

export const getConfigurationComparisonConfigTypes = ({
	vendor,
	nodes,
	version,
}: TCMConfigTypesDTO): Promise<TNameTitle[]> => {
	return client.post(`/${configurationComparison}/types`, { vendor, nodes, version });
};

export const getCCMavenirDates = ({
	vendor,
	nodes,
	version,
	type,
}: TMavenirDTO): Promise<string[]> => {
	return client.post(`/${configurationComparison}/options-dates`, { vendor, nodes, version, type });
};

export const getNodeTypes = ({ vendor, technology }: TCMNodeVendorTechnology): Promise<ICCOptionsProps[]> => {
	return client.post(nodeTypes, { vendor, technology });
};

export const getSiteIds = ({
	vendor,
	technology,
	nodeType,
}: TCMSitesIdsDTO): Promise<string[]> => {
	return client.post(siteIdsOptions, { vendor, technology, nodeType });
};

export const getCcDates = ({
	vendor,
	technology,
	nodeType,
	siteIds,
	timeZoneMinutes,
}: ICcDatesDTO): Promise<number[]> => {
	return client.post(datesCcModule, { vendor, technology, nodeType, siteIds, timeZoneMinutes });
};

export const getConfigurationComparisonVendors = (): Promise<TCMOptions<TNameTitle>[]> => {
	return client.get(configurationComparisonVendors);
};

export const getTree = ({
	dataSourceCreatedAtFirst,
	dataSourceCreatedAtSecond,
	isFirstCurrentDate,
	isSecondCurrentDate,
	nodeType,
	siteIds,
	technology,
	vendor,
	version,
	type,
	nodes,
	dataSourceCreatedAtFirstText,
	dataSourceCreatedAtSecondText,
}: TTreeCCModuleDTO): Promise<IMoTreeResponse> => {
	return client.post(treeCcModule, {
		dataSourceCreatedAtFirst,
		dataSourceCreatedAtSecond,
		isFirstCurrentDate,
		isSecondCurrentDate,
		nodeType,
		siteIds,
		technology,
		vendor,
		version,
		type,
		nodes,
		dataSourceCreatedAtFirstText,
		dataSourceCreatedAtSecondText,
	});
};

export const getMoTreePaths = (dto: IBranchPaths): Promise<IBranchSelectedPaths[]> => {
	return client.post(`${configurationComparison}/trees/paths-node`, {
		path: dto.path,
		dumpId: dto.dumpId,
	});
};

export const getMoBranches = (data: IMoBranchesDTO): Promise<TMoBranchesResponse> => {
	return client.post(moBranchesGet, data);
};

export const getMoBranch = (data: TMoBranchDTO): Promise<ComparisonDetailsType> => {
	return client.post(`${moBranches}/${data.comparisonId}/details`, data);
};

export const getDetailsOptions = (data: TDetailsOptionsDTO): Promise<any> => {
	return client.get(`${moBranches}/${data.comparisonId}/managed-objects-ids`, { params: { moName: data.moName } });
};

export const getDeltasBranch = (comparisonId: string): Promise<TNameTitle[]> => {
	return client.get(`${moBranches}/${comparisonId}/deltas`);
};

export const getMoNames = (comparisonId: string): Promise<string[]> => {
	return client.get(`${moBranches}/${comparisonId}/managed-objects-names`);
};

export const getParametersBranch = (comparisonId: string): Promise<string[]> => {
	return client.get(`${moBranches}/${comparisonId}/parameters`);
};

export const getSiteIdsBranch = (comparisonId: string): Promise<string[]> => {
	return client.get(`${moBranches}/${comparisonId}/siteids`);
};

export const getGnbIdsBranch = (comparisonId: string): Promise<string[]> => {
	return client.get(`${moBranches}/${comparisonId}/gnbIds`);
};

export const getConfigIdsBranch = (comparisonId: string): Promise<string[]> => {
	return client.get(`${moBranches}/${comparisonId}/configIds`);
};

export const runProcessMoBranches = (data: Partial<IMoBranchesDTO>): Promise<string> => {
	return client.post(moBranchesRun, data);
};

export const startProgress = (): Promise<void> => {
	return client.post(moBranchesProgress, {});
};

export const getProgress = (id: string): Promise<IProgressBranches> => {
	return client.get(`${moBranchesProgress}/${id}`);
};

export const exportMoBranch = (data: TMutateExportMoBranchDTO): Promise<void> => {
	return client.post(exportBranch, data);
};

export const exportMoBranches = (data: {
	dumpId?: string;
	ids: Array<string> | null;
}): Promise<void> => {
	return client.post(exportBranches, data);
};

export const createPreset = (data: ICreatePresetDTO): Promise<void> => {
	return client.post(createTreePreset, data);
};

export const editPreset = (data: TIdName): Promise<void> => {
	return client.post(updateTreePreset, data);
};

export const removePreset = ({ id }: IId): Promise<void> => {
	return client.delete(`${deleteTreePreset}/${id}`);
};

export const getPresets = (data: TCMSitesIdsDTO): Promise<IListOfTreePresets[]> => {
	return client.post(treePresets, {
		...data,
		sortParam: defaultSortParamsForPresetsOptions,
	});
};

export const getSelectedPresetTree = (presetTreeId: string): Promise<void> => {
	return client.get(`${selectedPresetTree}/${presetTreeId}`);
};
